import React from 'react'

import { Box, Icon, Text, Touchable, useUnsafeEffect } from '@factorialco/deprecated-design-system'
import type { LexicalCommand } from 'lexical'
import { COMMAND_PRIORITY_EDITOR, createCommand } from 'lexical'

import { useEditor } from 'modules/core/components/TextEditor/useEditor'

interface FileComponentProps {
  filename: string
  editable: boolean
  onClick: () => void
}

export const ATTACH_FILES_COMMAND: LexicalCommand<object> = createCommand()

export const CLEAR_FILES_COMMAND: LexicalCommand<object> = createCommand()

function getFileNameAndExtension(fullFilename: string) {
  const filename = fullFilename.replace(/\.[^/.]+$/, '')
  const extension = fullFilename.split('.').pop()

  return [filename, extension]
}

const File = ({ filename, editable, onClick }: FileComponentProps) => {
  const [_filename, extension] = getFileNameAndExtension(filename)

  return (
    <Box
      background="grey200"
      flexDirection="row"
      alignItems="center"
      paddingX="s8"
      paddingY="s6"
      maxWidth="s240"
      borderRadius={{
        all: 'abs006',
      }}
      style={{ marginBottom: '8px', marginRight: '8px' }}
    >
      <Box flexDirection="row" alignItems="center">
        <Box style={{ marginRight: '4px' }}>
          <Icon.Medium icon="file-filled" color="grey800" />
        </Box>
        <Box flexDirection="row" alignItems="center">
          <Box maxWidth="s120">
            <Text.Normal size="100" weight="medium" color="grey1000" ellipsis overflowWrap>
              {_filename}.
            </Text.Normal>
          </Box>
          <Text.Normal size="100" weight="medium" color="grey1000">
            {extension}
          </Text.Normal>
        </Box>
        {editable && (
          <Box style={{ marginLeft: '6px' }}>
            <Touchable onClick={onClick}>
              <Icon.Small icon="close" color="grey800" />
            </Touchable>
          </Box>
        )}
      </Box>
    </Box>
  )
}

interface Props {
  onFiles?: (files: File[]) => void
  initialFiles?: File[]
}

export default function FileList({ onFiles, initialFiles }: Props) {
  const editor = useEditor()
  const [files, setFiles] = React.useState<File[]>(initialFiles || [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useUnsafeEffect(() => onFiles && onFiles(files), [files])

  const handleOnClick = (itemIndex: number) => {
    if (!editor.isEditable()) {
      return
    }

    const filteredFileList = files.filter((_file, index) => index !== itemIndex)

    setFiles(filteredFileList)
  }

  useUnsafeEffect(() => {
    return editor.registerCommand(
      ATTACH_FILES_COMMAND,
      (fileList: File[]) => {
        if (!editor.isEditable()) {
          return false
        }

        setFiles((currentFiles) => [...currentFiles, ...fileList])

        return false
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor, onFiles])

  useUnsafeEffect(() => {
    return editor.registerCommand(
      CLEAR_FILES_COMMAND,
      () => {
        if (!editor.isEditable()) {
          return false
        }

        setFiles([])

        return false
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor, onFiles])

  const fileList = files.map((file: File, index: number) => {
    return (
      <File
        key={index}
        editable={editor.isEditable()}
        filename={file.name}
        onClick={() => handleOnClick(index)}
      />
    )
  })

  return files.length ? (
    <Box paddingX="s16" paddingBottom="s8" flexDirection="row" flexWrap="wrap" alignItems="center">
      {fileList}
    </Box>
  ) : null
}
